import { Button, Image, Modal, Space, Typography, message } from "antd";
import { useContext, useState } from "react";
import { EnvContext } from "../../../../../providers/env-provider";
import { refreshPlayerPhoto } from "../../Providers/refresh-player-photo-provider";

const { Text } = Typography;
const PLAYER_PHOTO_S3_URL = "https://s3.fnl.pro:9000";

type RefreshPlayerPhotoProps = {
  fnlLeagueId?: number | string;
  fnlPlayerId?: number | string;
};

type RequestError = {
  response?: {
    data?:
      | string
      | {
          message?: string;
        };
  };
  message?: string;
};

const getErrorMessage = (error: unknown) => {
  const requestError = error as RequestError;
  const responseData = requestError.response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return (
    responseData?.message || requestError.message || "Не удалось обновить фото"
  );
};

const getDateParam = () => new Date().toISOString();

export const RefreshPlayerPhoto = ({
  fnlLeagueId,
  fnlPlayerId,
}: RefreshPlayerPhotoProps) => {
  const { THUMBOR_URL } = useContext(EnvContext);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [beforeDate, setBeforeDate] = useState(getDateParam());
  const [afterDate, setAfterDate] = useState<string | null>(null);
  const disabled = !fnlLeagueId || !fnlPlayerId;
  const s3Url = (THUMBOR_URL || PLAYER_PHOTO_S3_URL).replace(/\/$/, "");

  const getPlayerPhotoUrl = (date: string) =>
    `${s3Url}/fnl/player/player-${fnlLeagueId}-${fnlPlayerId}?date=${encodeURIComponent(
      date
    )}`;

  const handleOpen = () => {
    setBeforeDate(getDateParam());
    setAfterDate(null);
    setIsOpen(true);
  };

  const handleRefresh = async () => {
    if (disabled) {
      return;
    }

    setLoading(true);

    try {
      const data = await refreshPlayerPhoto({
        leagueId: fnlLeagueId,
        playerId: fnlPlayerId,
      });

      setAfterDate(getDateParam());
      message.success(
        data.s3Url
          ? `Фото игрока обновлено: ${data.s3Url}`
          : "Фото игрока обновлено"
      );
    } catch (error) {
      message.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="small" onClick={handleOpen} disabled={disabled} block>
        Обновить фото игрока
      </Button>

      <Modal
        title="Обновить фото игрока"
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        okText="Обновить фото"
        cancelText="Закрыть"
        confirmLoading={loading}
        onOk={handleRefresh}
        width={760}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space
            size={16}
            align="start"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <Space direction="vertical" size={8} style={{ width: "50%" }}>
              <Text strong>До обновления</Text>
              <Image
                width="100%"
                src={getPlayerPhotoUrl(beforeDate)}
                alt="Фото игрока до обновления"
                fallback="/images/logos/fnl-logo.png"
              />
            </Space>

            <Space direction="vertical" size={8} style={{ width: "50%" }}>
              <Text strong>После обновления</Text>
              {afterDate ? (
                <Image
                  width="100%"
                  src={getPlayerPhotoUrl(afterDate)}
                  alt="Фото игрока после обновления"
                  fallback="/images/logos/fnl-logo.png"
                />
              ) : (
                <Text type="secondary">
                  Нажмите «Обновить фото», чтобы загрузить новую версию
                </Text>
              )}
            </Space>
          </Space>
        </Space>
      </Modal>
    </>
  );
};
